const DEFAULT_RETURN_TO = '/';

const BLOCKED_RETURN_TO_PATHS = ['/callback', '/code', '/logout'] as const;

const getOrigin = (): string => window.location.origin;

const isBlockedPath = (pathname: string): boolean =>
  BLOCKED_RETURN_TO_PATHS.some((blockedPath) => pathname === blockedPath || pathname.startsWith(`${blockedPath}/`));

const getRelativeReturnTo = (returnTo: string, origin: string): string | null => {
  if (returnTo.startsWith('/') && !returnTo.startsWith('//')) {
    return returnTo;
  }

  let url: URL;
  try {
    url = new URL(returnTo);
  } catch {
    return null;
  }

  if (url.origin !== origin) {
    return null;
  }

  return url.pathname + url.search + url.hash;
};

const getAllowedReturnTo = (returnTo: string | null | undefined, origin: string): string | null => {
  if (!returnTo) {
    return null;
  }

  const relativeReturnTo = getRelativeReturnTo(returnTo, origin);
  if (!relativeReturnTo) {
    return null;
  }

  const url = new URL(relativeReturnTo, origin);
  if (isBlockedPath(url.pathname) || url.searchParams.has('code')) {
    return null;
  }

  return relativeReturnTo;
};

/**
 * Returns a safe same-origin redirect target for login/logout flows.
 */
export const sanitizeReturnTo = (
  returnTo: string | null | undefined,
  options?: { defaultReturnTo?: string; origin?: string }
): string => {
  const origin = options?.origin || getOrigin();
  const allowedReturnTo = getAllowedReturnTo(returnTo, origin);
  if (allowedReturnTo) {
    return allowedReturnTo;
  }

  const allowedDefaultReturnTo = getAllowedReturnTo(options?.defaultReturnTo, origin);
  return allowedDefaultReturnTo || DEFAULT_RETURN_TO;
};

/**
 * Returns a safe same-origin redirect target, or null when the value should not be persisted.
 */
export const getSafeReturnToOrNull = (
  returnTo: string | null | undefined,
  options?: { origin?: string }
): string | null => getAllowedReturnTo(returnTo, options?.origin || getOrigin());
